import { isDefined, isEmpty } from '@sgme/fp';
import type { IntlKey } from 'locales';
import { sortBy } from 'lodash-es';
import type { ProductVariation, SelectedProductVariation } from 'models/productsReferential';
import type { IntlShape } from 'react-intl';
import type { Predicate } from 'state/utils/types';

function filterProductVariations(
  productVariations: readonly ProductVariation[],
  predicate: (productVariation: ProductVariation) => boolean,
) {
  const filteredProductVariations = productVariations.filter(predicate);
  return isEmpty(filteredProductVariations) ? productVariations : filteredProductVariations;
}

function makeFilterProductVariationsPredicates(selected: Partial<SelectedProductVariation>) {
  const filterProductVariationsPredicates: Predicate<ProductVariation>[] = [];
  if (isDefined(selected.productType)) {
    filterProductVariationsPredicates.push(({ productType }) => productType === selected.productType);
  }
  if (isDefined(selected.productSubType)) {
    filterProductVariationsPredicates.push(({ productSubType }) => productSubType === selected.productSubType);
  }
  if (isDefined(selected.wrapper)) {
    filterProductVariationsPredicates.push(({ wrapper }) => wrapper === selected.wrapper);
  }
  if (isDefined(selected.assetType) && selected.assetType !== 'All') {
    filterProductVariationsPredicates.push(({ assetTypes = [] }) => assetTypes.includes(selected.assetType!));
  }
  if (isDefined(selected.payoffType)) {
    filterProductVariationsPredicates.push(({ payoffTypes = [] }) => payoffTypes.includes(selected.payoffType!));
  }
  return filterProductVariationsPredicates;
}

export function autoSelectProductVariation(
  selectedProductVariation: Partial<SelectedProductVariation>,
  availableProductVariations: ProductVariation[],
  intl: IntlShape,
): Partial<SelectedProductVariation> {
  const filteredProductVariations = makeFilterProductVariationsPredicates(selectedProductVariation).reduce(
    filterProductVariations,
    availableProductVariations,
  );

  const sortByTranslatedProductReferential = sortBy(filteredProductVariations, [
    p => intl.formatMessage({ id: `quote.products.${p.productSubType}` as IntlKey }),
    p => intl.formatMessage({ id: `quote.products.${p.wrapper}` as IntlKey }),
  ]);

  return { ...selectedProductVariation, ...sortByTranslatedProductReferential[0] };
}
